import axios, { AxiosRequestConfig } from 'axios';
import { obtemFirebaseToken } from '../mongo-atlas/autenticacao';
import { urlAPI } from 'src/config';
import { FarmaError } from 'src/integracoes/modelos/erros';

// Instâncias Axios
const apiClient = axios.create({
  baseURL: urlAPI,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true
});

const apiPublicClient = axios.create({
  baseURL: urlAPI,
  headers: { 'Content-Type': 'application/json' }
});

// Interceptor de autenticação (Firebase)
apiClient.interceptors.request.use(async (config) => {
  const firebaseIdToken = await obtemFirebaseToken();
  if (firebaseIdToken) {
    config.headers.Authorization = `Bearer ${firebaseIdToken}`;
  }
  return config;
});

// Função genérica para todas as requisições
async function request<T>(
  method: 'get' | 'post' | 'patch' | 'put' | 'delete',
  url: string,
  body?: any,
  config?: AxiosRequestConfig,
  usePublicClient = false
): Promise<T> {
  const client = usePublicClient ? apiPublicClient : apiClient;
  try {
    const { data } = await client.request<T>({ method, url, data: body, ...config });
    return data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const backendError = err.response?.data;
      throw new FarmaError({
        nome: backendError?.error || 'API_ERROR',
        mensagem:
          backendError?.mensagem || backendError?.message || 'Falha ao comunicar com o servidor',
        causa: backendError
      });
    }
    // erro genérico não relacionado ao Axios
    throw new FarmaError({
      nome: 'FRONT_ERROR',
      mensagem: err?.mensagem || err?.message || 'Erro desconhecido',
      causa: err
    });
  }
}

// Métodos HTTP (usando request genérico)
export const get = <T>(url: string, config?: AxiosRequestConfig) =>
  request<T>('get', url, undefined, config);

export const post = <T>(url: string, body: any, config?: AxiosRequestConfig) =>
  request<T>('post', url, body, config);

export const patch = <T>(url: string, body: any, config?: AxiosRequestConfig) =>
  request<T>('patch', url, body, config);

export const put = <T>(url: string, body: any, config?: AxiosRequestConfig) =>
  request<T>('put', url, body, config);

export const del = <T>(url: string, config?: AxiosRequestConfig) =>
  request<T>('delete', url, undefined, config);

// Versão pública (sem autenticação)
export const patchAtivaUsuario = <T>(url: string, body: any, config?: AxiosRequestConfig) =>
  request<T>('patch', url, body, config, true);

// Função específica para fechamento de abas (usa fetch + keepalive para garantir envio)
export async function postKeepAlive(url: string, body: any) {
  const firebaseIdToken = await obtemFirebaseToken();
  const fullUrl = url.startsWith('http') ? url : `${urlAPI}${url}`;

  return fetch(fullUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(firebaseIdToken ? { Authorization: `Bearer ${firebaseIdToken}` } : {})
    },
    body: JSON.stringify(body),
    keepalive: true
  });
}

export default apiClient;
